import { currentAppEnvConfigs } from '@/configs/env'

import { defaultFetcher, FetcherFunctionType } from '.'
import { checkBusinessErrorWith200Response } from './hooks'
import { getApiRequestBaseUrl, Version } from './utils'

/* --------------------------- Price basic service -------------------------- */

const commonPriceApiConfig = {
  prefix: '/price',
  serverDomain: currentAppEnvConfigs.backendHosts.price,
  serverDomainInternal: currentAppEnvConfigs.backendHosts.priceInternal,
}
export const priceApiBaseUrl = getApiRequestBaseUrl(commonPriceApiConfig)
export const priceFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(priceApiBaseUrl + path, requestOptions, {
    afterResponseHooks: [checkBusinessErrorWith200Response],
    ...extendedOptions,
  })

const priceApiBaseUrlV2 = getApiRequestBaseUrl({
  ...commonPriceApiConfig,
  version: Version.V2,
})
export const priceFetcherV2: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(priceApiBaseUrlV2 + path, ...restParams)

const priceApiBaseUrlV3 = getApiRequestBaseUrl({
  ...commonPriceApiConfig,
  version: Version.V3,
})
export const priceFetcherV3: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(priceApiBaseUrlV3 + path, ...restParams)

/* ------------------------------ Meta service ------------------------------ */

const commonMetaApiConfig = {
  serverDomain: currentAppEnvConfigs.backendHosts.price,
  serverDomainInternal: currentAppEnvConfigs.backendHosts.tokenMetaInternal,
  prefix: '/meta',
}

export const metaApiBaseUrl = getApiRequestBaseUrl(commonMetaApiConfig)
export const metaFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(metaApiBaseUrl + path, ...restParams)

const metaApiBaseUrlV2 = getApiRequestBaseUrl({
  ...commonMetaApiConfig,
  version: Version.V2,
})
export const metaFetcherV2: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(metaApiBaseUrlV2 + path, ...restParams)

const commonMarketApiConfig = {
  ...commonPriceApiConfig,
  prefix: '/market',
}

const marketApiBaseUrl = getApiRequestBaseUrl(commonMarketApiConfig)
export const marketFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(marketApiBaseUrl + path, ...restParams)

const marketApiBaseUrlV2 = getApiRequestBaseUrl({
  ...commonMarketApiConfig,
  version: Version.V2,
})
export const marketFetcherV2: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(marketApiBaseUrlV2 + path, ...restParams)

/* ----------------------------- Price Member service ----------------------------- */
const commonPriceMemberApiConfig = {
  prefix: '/member',
  serverDomain: currentAppEnvConfigs.backendHosts.priceMember,
}

const priceMemberApiBaseUrlV2 = getApiRequestBaseUrl({
  ...commonPriceMemberApiConfig,
  version: Version.V2,
})

export const memberFetcherV2: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(priceMemberApiBaseUrlV2 + path, requestOptions, {
    ...extendedOptions,
  })

/* ----------------------------- Popular service ---------------------------- */

const popularApiBaseUrl = getApiRequestBaseUrl({
  ...commonPriceApiConfig,
  prefix: '/popular',
})
export const popularFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(popularApiBaseUrl + path, requestOptions, {
    ...extendedOptions,
  })

/* ----------------------------- App Public API ----------------------------- */

const commonAppPublicApiConfig = {
  prefix: '/api/v1/public',
  serverDomain: currentAppEnvConfigs.backendHosts.app,
  withVersion: false,
}

export const appPublicApiBaseUrl = getApiRequestBaseUrl(commonAppPublicApiConfig)
export const appPublicFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(appPublicApiBaseUrl + path, requestOptions, {
    afterResponseHooks: [checkBusinessErrorWith200Response],
    ...extendedOptions,
  })

/* --------------------------- Payload CMS service -------------------------- */

const commonPayloadCmsApiConfig = {
  prefix: '/api',
  serverDomain: currentAppEnvConfigs.payloadCmsDomain,
}
export const payloadCmsApiBaseUrl = getApiRequestBaseUrl(commonPayloadCmsApiConfig)
export const payloadCmsFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(payloadCmsApiBaseUrl + path, ...restParams)

/* --------------------------- coinsaiSunshine service -------------------------- */

const commonMarketfeedApiConfig = {
  prefix: '/marketfeed',
  serverDomain: currentAppEnvConfigs.backendHosts.coinsaiSunshine,
}
export const marketfeedApiBaseUrl = getApiRequestBaseUrl(commonMarketfeedApiConfig)
export const marketfeedFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(marketfeedApiBaseUrl + path, requestOptions, {
    ...extendedOptions,
  })

const coinsaiSunshineTokenApiConfig = {
  prefix: '/token',
  serverDomain: currentAppEnvConfigs.backendHosts.coinsaiSunshine,
}
export const coinsaiSunshineTokenApiBaseUrl = getApiRequestBaseUrl(coinsaiSunshineTokenApiConfig)
export const coinsaiSunshineTokenFetcher: FetcherFunctionType = (
  path,
  requestOptions,
  extendedOptions,
) =>
  defaultFetcher(coinsaiSunshineTokenApiBaseUrl + path, requestOptions, {
    ...extendedOptions,
  })

const cryptoCoinInfoApiConfig = {
  prefix: '/api',
  serverDomain: currentAppEnvConfigs.backendHosts.cryptoCoinInfo,
}
export const cryptoCoinInfoApiBaseUrl = getApiRequestBaseUrl({
  ...cryptoCoinInfoApiConfig,
  version: Version.V2,
})
export const cryptoCoinInfoFetcherV2: FetcherFunctionType = (
  path,
  requestOptions,
  extendedOptions,
) =>
  defaultFetcher(cryptoCoinInfoApiBaseUrl + path, requestOptions, {
    ...extendedOptions,
  })

/* --------------------------- Token Management service -------------------------- */

const tokenMgmtApiConfig = {
  prefix: '/meta',
  serverDomain: currentAppEnvConfigs.backendHosts.tokenMgmt,
  withVersion: false,
}
export const tokenMgmtApiBaseUrl = getApiRequestBaseUrl(tokenMgmtApiConfig)
export const tokenMgmtFetcher: FetcherFunctionType = (path, ...restParams) =>
  defaultFetcher(tokenMgmtApiBaseUrl + path, ...restParams)

/* --------------------------- Equity Market Data service -------------------------- */

const equityMarketDataApiConfig = {
  prefix: '/api',
  serverDomain: currentAppEnvConfigs.backendHosts.equityMarketData,
}
export const equityMarketDataApiBaseUrl = getApiRequestBaseUrl(equityMarketDataApiConfig)
export const equityMarketDataFetcher: FetcherFunctionType = (
  path,
  requestOptions,
  extendedOptions,
) =>
  defaultFetcher(equityMarketDataApiBaseUrl + path, requestOptions, {
    ...extendedOptions,
  })

/* --------------------------- Knock-Out Predictions service -------------------------- */

const knockOutApiConfig = {
  prefix: '/predictions/public',
  serverDomain: currentAppEnvConfigs.backendHosts.knockOut,
  withVersion: false,
}

const knockOutApiBaseUrl = getApiRequestBaseUrl(knockOutApiConfig)

export const knockOutFetcher: FetcherFunctionType = (path, requestOptions, extendedOptions) =>
  defaultFetcher(knockOutApiBaseUrl + path, requestOptions, {
    ...extendedOptions,
  })
